import { CCEntityType, CC_ENTITY_CONFIGS, CCEntityConfig } from "components/CC/types/entityConfig";
import { createContext, ReactNode, useContext, useMemo } from "react";

interface CCEntityContextValue {
    /** The entity type this CC instance is for */
    entityType: CCEntityType;
    /** The entity ID (e.g., formStructureId, integrationId) */
    entityId: string | undefined;
    /** The saved scenario linked to the current entity, when available */
    scenarioId?: string | null;
    /** Configuration for this entity type */
    config: CCEntityConfig;
    /**
     * Form fields from the connected form structure.
     * Only available for Integration entity type (which has a connected FormStructure).
     * Used by ParameterPicker to show available form fields.
     */
    formFields?: App.Data.FormFieldData[];
    /**
     * Integration custom fields used as "keys" in ParameterPicker.
     * Available for Integration entity type.
     */
    customFields?: App.Data.IntegrationCustomFieldData[];
}

const CCEntityContext = createContext<CCEntityContextValue | null>(null);

interface CCEntityProviderProps {
    entityType: CCEntityType;
    entityId: string | undefined;
    scenarioId?: string | null;
    /** Form fields from form structure - only for Integration */
    formFields?: App.Data.FormFieldData[];
    /** Integration custom fields - only for Integration */
    customFields?: App.Data.IntegrationCustomFieldData[];
    children: ReactNode;
}

export const CCEntityProvider = ({
    entityType,
    entityId,
    scenarioId,
    formFields,
    customFields,
    children,
}: CCEntityProviderProps) => {
    const value = useMemo(
        () => ({
            entityType,
            entityId,
            scenarioId,
            config: CC_ENTITY_CONFIGS[entityType],
            formFields,
            customFields,
        }),
        [entityType, entityId, scenarioId, formFields, customFields]
    );

    return <CCEntityContext.Provider value={value}>{children}</CCEntityContext.Provider>;
};

export const useCCEntity = () => {
    const context = useContext(CCEntityContext);
    if (!context) {
        throw new Error("useCCEntity must be used within CCEntityProvider");
    }
    return context;
};

/**
 * Optional hook that returns null if not in CCEntityContext
 * Useful for components that can work with or without entity context
 */
export const useCCEntityOptional = () => {
    return useContext(CCEntityContext);
};
